/**
 * Physical addresses.
 *
 * A location is `container / bin` and a card's full address adds `/ #slot`,
 * e.g. `BOX-7 / DRAWER-2 / #147`. The slot is the append order inside the bin,
 * which is what makes chaos sorting work: the user never re-files anything, so
 * the address has to be recorded once and remain findable.
 *
 * The same string is what lands in CSV exports and imports, so parsing it back
 * has to be strict rather than clever.
 */
export const ADDRESS_SEPARATOR = ' / ';
export const SLOT_PREFIX = '#';
function assertLabel(label, what) {
    if (typeof label !== 'string')
        throw new TypeError(`${what} label must be a string.`);
    const trimmed = label.trim();
    if (trimmed === '')
        throw new Error(`${what} label must not be empty.`);
    if (trimmed !== label) {
        throw new Error(`${what} label "${label}" must not have leading or trailing whitespace.`);
    }
    if (label.includes('/')) {
        throw new Error(`${what} label "${label}" must not contain the address separator "/".`);
    }
    return label;
}
export function formatSlotLabel(slotNo) {
    if (!Number.isInteger(slotNo) || slotNo <= 0) {
        throw new Error(`A slot number must be a positive integer, received ${slotNo}.`);
    }
    return `${SLOT_PREFIX}${slotNo}`;
}
export function formatBinAddress(container, bin) {
    return `${assertLabel(container.label, 'Container')}${ADDRESS_SEPARATOR}${assertLabel(bin.label, 'Bin')}`;
}
export function formatCopyAddress(container, bin, slotNo) {
    return `${formatBinAddress(container, bin)}${ADDRESS_SEPARATOR}${formatSlotLabel(slotNo)}`;
}
/**
 * Parse `BOX-7 / DRAWER-2` or `BOX-7 / DRAWER-2 / #147`.
 *
 * Throws on anything else. A silently half-parsed address is worse than a
 * failed import: it would put a card's recorded location somewhere the user
 * will never look.
 */
export function parseCopyAddress(address) {
    if (typeof address !== 'string' || address.trim() === '') {
        throw new Error('An address must be a non-empty string.');
    }
    const parts = address.split(ADDRESS_SEPARATOR).map((part) => part.trim());
    if (parts.length < 2) {
        throw new Error(`Address "${address}" must have at least two segments: container / bin.`);
    }
    if (parts.length > 3) {
        throw new Error(`Address "${address}" has too many segments; expected container / bin or container / bin / #slot.`);
    }
    const [containerLabel, binLabel, slotPart] = parts;
    if (!containerLabel || !binLabel) {
        throw new Error(`Address "${address}" has an empty container or bin segment.`);
    }
    let slotNo = null;
    if (slotPart !== undefined) {
        if (!slotPart.startsWith(SLOT_PREFIX)) {
            throw new Error(`Slot segment "${slotPart}" must start with "#", as in "#147".`);
        }
        slotNo = Number(slotPart.slice(SLOT_PREFIX.length));
        if (!Number.isInteger(slotNo) || slotNo <= 0) {
            throw new Error(`Slot segment "${slotPart}" must be a positive integer.`);
        }
    }
    return { containerLabel, binLabel, slotNo };
}
/** Order a walking route: container, then bin, then slot. */
export function compareWalkOrder(a, b) {
    if (a.containerSortOrder !== b.containerSortOrder)
        return a.containerSortOrder - b.containerSortOrder;
    if (a.binSortOrder !== b.binSortOrder)
        return a.binSortOrder - b.binSortOrder;
    return (a.slotNo ?? -1) - (b.slotNo ?? -1);
}
//# sourceMappingURL=location.js.map